import React, { useEffect, useRef, useState } from 'react'
import { getWacomStu430, isWebHidSupported } from './wacomStu430'

export interface UseWacomPenInputOptions<K extends string> {
  /** Map of slot name → ref to a react-canvas-draw component instance. */
  canvases: Record<K, React.RefObject<any>>
  /** Slot that should currently receive Wacom pen input (e.g. 'doctor' or 'single'). */
  active: K
  /**
   * Called ~150ms after the user finishes a Wacom stroke so the host can
   * push the latest canvas pixels into its own state/Redux (typically by
   * invoking its existing `renderCanvas(slot)` function).
   */
  onStrokeCommit: (slot: K) => void
  /** Auto-wipe the pad LCD when `active` changes. Default true. */
  clearOnSlotChange?: boolean
  /** Canvas brush width in pixels. Default 2. */
  strokeWidth?: number
  /** Stroke colour. Default '#000'. */
  strokeColor?: string
}

export interface UseWacomPenInputResult {
  supported: boolean
  ready: boolean
  connecting: boolean
  /** Model name reported by the pad (e.g. "STU-430"). Null until connected. */
  deviceName: string | null
  /** Trigger the WebHID permission picker (must be called from a user gesture). */
  connect: () => Promise<void>
  /** Wipe the pad's physical LCD. Safe to call when not connected. */
  clearPad: () => void
  /**
   * Release the USB HID handle so another tab/window can claim the pad.
   * Fire-and-forget — returns immediately so callers can use it from a
   * synchronous user-gesture handler (e.g. just before `window.open`).
   */
  releasePad: () => void
}

/**
 * Wires Wacom STU-430 pen input into one or more `react-canvas-draw` canvases.
 *
 * Pen packets are painted directly onto the active canvas's bitmap
 * (`canvasContainer.childNodes[1]`) — the same node the host already calls
 * `toDataURL` on — so existing save flows keep working unchanged.
 */
export function useWacomPenInput<K extends string>(opts: UseWacomPenInputOptions<K>): UseWacomPenInputResult {
  const {
    canvases,
    active,
    onStrokeCommit,
    clearOnSlotChange = true,
    strokeWidth = 2,
    strokeColor = '#000',
  } = opts

  const supported = isWebHidSupported()
  const [ready, setReady] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [deviceName, setDeviceName] = useState<string | null>(null)

  // Stable refs so the long-lived pen subscription always sees the latest values.
  const activeRef = useRef(active)
  useEffect(() => { activeRef.current = active }, [active])
  const canvasesRef = useRef(canvases)
  useEffect(() => { canvasesRef.current = canvases }, [canvases])
  const onCommitRef = useRef(onStrokeCommit)
  useEffect(() => { onCommitRef.current = onStrokeCommit }, [onStrokeCommit])

  // Silent reconnect on mount + whenever the tab regains focus. Retries
  // because the pad may be held by another tab (e.g. the user just clicked
  // "open in new tab") and needs a moment to release.
  useEffect(() => {
    if (!supported) return
    let cancelled = false
    let inFlight = false
    const svc = getWacomStu430()

    const markReady = () => {
      setReady(true)
      setDeviceName(svc.info?.deviceName ?? null)
    }
    const tryReconnect = async (attemptsLeft: number, delayMs: number) => {
      if (cancelled || inFlight) return
      if (svc.isConnected()) { markReady(); return }
      inFlight = true
      try {
        const ok = await svc.reconnect()
        if (cancelled) return
        if (ok) { markReady(); inFlight = false; return }
      } catch {
        if (cancelled) return
      }
      inFlight = false
      if (attemptsLeft > 0 && !cancelled) {
        setTimeout(() => tryReconnect(attemptsLeft - 1, delayMs), delayMs)
      }
    }

    // Initial attempt on mount.
    tryReconnect(6, 400)

    // Reattempt when the tab becomes active again — typically right after
    // the user closes the secondary tab that was holding the pad.
    const onVisible = () => {
      if (document.visibilityState === 'visible') tryReconnect(3, 300)
    }
    const onFocus = () => tryReconnect(3, 300)
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onFocus)

    // Track physical connect/disconnect so the UI reflects the pad being
    // plugged or unplugged (and the service's auto-reopen on hid 'connect').
    const offConn = svc.onConnection((state) => {
      if (state === 'connect') markReady()
      else if (state === 'disconnect') { setReady(false); setDeviceName(null) }
      // 'connecting' is purely informational for the global toast — the
      // hook leaves `ready` alone until 'connect' or 'disconnect' arrives.
    })

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onFocus)
      offConn()
    }
  }, [supported])

  // Auto-clear the pad LCD whenever the active signer switches.
  useEffect(() => {
    if (!ready || !clearOnSlotChange) return
    getWacomStu430().clearScreen().catch(() => undefined)
  }, [active, ready, clearOnSlotChange])

  // Pen subscription: paint onto the active canvas bitmap + debounce-commit.
  useEffect(() => {
    if (!ready) return
    const svc = getWacomStu430()
    let lastPoint: { x: number; y: number; t: number } | null = null
    let timer: ReturnType<typeof setTimeout> | null = null
    const off = svc.onPen((p) => {
      const slot = activeRef.current
      const ref = canvasesRef.current[slot]
      const node = ref?.current?.canvasContainer?.childNodes?.[1] as HTMLCanvasElement | undefined
      if (!node) return
      const ctx = node.getContext('2d')
      if (!ctx) return
      const { x: denomX, y: denomY } = svc.getCoordDenominator()
      const sx = (p.xRaw / denomX) * node.width
      const sy = (p.yRaw / denomY) * node.height
      const now = performance.now()
      if (lastPoint && now - lastPoint.t > 80) lastPoint = null
      const inBounds = sx >= 0 && sy >= 0 && sx <= node.width && sy <= node.height
      if (p.drawing && inBounds) {
        ctx.lineWidth = strokeWidth
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        ctx.strokeStyle = strokeColor
        if (lastPoint) {
          ctx.beginPath()
          ctx.moveTo(lastPoint.x, lastPoint.y)
          ctx.lineTo(sx, sy)
          ctx.stroke()
        } else {
          ctx.beginPath()
          ctx.arc(sx, sy, strokeWidth / 2, 0, Math.PI * 2)
          ctx.fillStyle = strokeColor
          ctx.fill()
        }
        lastPoint = { x: sx, y: sy, t: now }
        if (timer) clearTimeout(timer)
        timer = setTimeout(() => onCommitRef.current(activeRef.current), 150)
      } else if (!p.drawing) {
        lastPoint = null
      }
    })
    return () => {
      off()
      if (timer) clearTimeout(timer)
    }
  }, [ready, strokeWidth, strokeColor])

  const connect = async () => {
    if (connecting) return
    setConnecting(true)
    try {
      const svc = getWacomStu430()
      const ok = await svc.requestAndConnect()
      if (ok) {
        setReady(true)
        setDeviceName(svc.info?.deviceName ?? null)
      }
    } catch (e) {
      console.error('Wacom connect failed', e)
    } finally {
      setConnecting(false)
    }
  }

  const clearPad = () => {
    if (ready) getWacomStu430().clearScreen().catch(() => undefined)
  }

  const releasePad = () => {
    getWacomStu430().disconnect().catch(() => undefined)
    setReady(false)
    setDeviceName(null)
  }

  return { supported, ready, connecting, deviceName, connect, clearPad, releasePad }
}
